import photoManifest from "../../../docs/assets/places.json";
import routeSnapshot from "./route-snapshot.json";
import { additionalEditions } from "./gateway-expansion";

export type CoreAirportCode =
	| "FUK"
	| "KIX"
	| "NRT"
	| "HND"
	| "CTS"
	| "OKA"
	| "NGO"
	| "KMJ";
export type AirportCode =
	| CoreAirportCode
	| "SHI"
	| "SDJ"
	| "HSG"
	| "HKD"
	| "KKJ"
	| "OIT"
	| "KMI"
	| "KMQ"
	| "OKJ"
	| "OBO"
	| "HIJ"
	| "TKS"
	| "KIJ"
	| "MYJ"
	| "ISG"
	| "YGJ"
	| "IBR"
	| "NGS"
	| "AOJ"
	| "FSZ"
	| "TAK"
	| "KOJ";
export type OriginCode =
	| "ICN"
	| "GMP"
	| "PUS"
	| "CJU"
	| "TAE"
	| "CJJ"
	| "MWX"
	| "YNY";

export type AccessBranch = {
	id: string;
	destination: string;
	mode: string;
	minutes: string;
	fare: string;
	boarding: string;
	fallback: string;
};

export type Edition = {
	iata: AirportCode;
	city: string;
	airportName: string;
	mood: string;
	fit: string;
	accessSource: string;
	branches: AccessBranch[];
	foods: string[];
	specialties: string[];
	prices: {
		transport: string;
		meal: string;
		experience: string;
		snack: string;
	};
	spots: string[];
};

export const origins: {
	iata: OriginCode;
	city: string;
	airportName: string;
}[] = [
	{ iata: "ICN", city: "인천", airportName: "인천국제공항" },
	{ iata: "GMP", city: "서울", airportName: "김포국제공항" },
	{ iata: "PUS", city: "부산", airportName: "김해국제공항" },
	{ iata: "CJU", city: "제주", airportName: "제주국제공항" },
	{ iata: "TAE", city: "대구", airportName: "대구국제공항" },
	{ iata: "CJJ", city: "청주", airportName: "청주국제공항" },
	{ iata: "MWX", city: "무안", airportName: "무안국제공항" },
	{ iata: "YNY", city: "양양", airportName: "양양국제공항" },
];

const coreEditions: Record<CoreAirportCode, Edition> = {
	FUK: {
		iata: "FUK",
		city: "후쿠오카",
		airportName: "후쿠오카공항",
		mood: "공항과 도심이 붙어 있어 짧은 일정도 골목과 포장마차까지 빨리 닿는 도시형 관문이에요.",
		fit: "첫 일본 · 2박 3일 · 미식",
		accessSource: "https://www.fukuoka-airport.jp/en/access/",
		branches: [
			{
				id: "acc-fuk-01",
				destination: "하카타",
				mode: "국제선 직행버스",
				minutes: "약 15–20분",
				fare: "공식 시간표에서 확인",
				boarding: "국제선 1층 버스 승강장에서 타요.",
				fallback:
					"놓치면 국내선 무료 셔틀로 약 10분 이동한 뒤 지하철을 이용해요.",
			},
			{
				id: "acc-fuk-02",
				destination: "텐진",
				mode: "버스 또는 셔틀+지하철",
				minutes: "약 30분",
				fare: "공식 시간표에서 확인",
				boarding: "짐이 많으면 국제선 직행버스를 먼저 봐요.",
				fallback: "버스가 지연되면 국내선 터미널로 이동해 지하철을 타요.",
			},
			{
				id: "acc-fuk-03",
				destination: "다자이후",
				mode: "국제선 버스",
				minutes: "약 25–35분",
				fare: "운행편별 확인",
				boarding: "도착일 운행 여부와 승강장을 먼저 확인해요.",
				fallback: "직행이 없으면 하카타나 텐진을 경유해요.",
			},
		],
		foods: [
			"하카타 돈코츠 라멘—면 익힘을 골라 늦은 밤",
			"모츠나베—둘 이상이 나누는 저녁",
			"미즈타키—담백한 닭 코스",
			"멘타이코—아침밥과 선물",
		],
		specialties: ["아마오 딸기 과자", "하카타 토리몬", "야메차"],
		prices: {
			transport: "도심 ¥260–500",
			meal: "보통 한 끼 ¥900–1,800",
			experience: "타워·전시 ¥800–2,400",
			snack: "간식·커피 ¥350–800",
		},
		spots: [
			"하카타역—첫 환승 거점",
			"캐널시티—하카타 도보권",
			"나카스—밤 골목",
			"오호리공원—지하철",
			"다자이후텐만구—공항·시내 버스",
			"모모치해변·후쿠오카타워—도심 서쪽",
		],
	},
	KIX: {
		iata: "KIX",
		city: "오사카",
		airportName: "간사이국제공항",
		mood: "오사카·교토·고베로 갈라지는 광역 관문이라 숙소 도시를 먼저 정해야 해요.",
		fit: "쇼핑 · 교토 연계 · 다도시",
		accessSource:
			"https://www.kansai-airport.or.jp/en/access/from-airport/train",
		branches: [
			{
				id: "acc-kix-01",
				destination: "난바",
				mode: "난카이 라피트·공항급행",
				minutes: "약 34–43분",
				fare: "열차별 확인",
				boarding: "1터미널 철도역, 2터미널은 셔틀 뒤 역에서 타요.",
				fallback: "철도 운휴 때는 난바행 공항버스를 확인해요.",
			},
			{
				id: "acc-kix-02",
				destination: "텐노지",
				mode: "JR 하루카",
				minutes: "약 30분",
				fare: "좌석별 확인",
				boarding: "JR 개찰에서 자유석과 지정석을 구분해요.",
				fallback: "운휴 시 난카이선으로 시내에 들어가 환승해요.",
			},
			{
				id: "acc-kix-03",
				destination: "교토",
				mode: "JR 하루카",
				minutes: "약 75분",
				fare: "좌석별 확인",
				boarding: "직통 편과 마지막 열차를 먼저 확인해요.",
				fallback: "막차 뒤에는 오사카행 버스와 공항 근처 숙박을 비교해요.",
			},
		],
		foods: [
			"오코노미야키—난바·우메다 철판",
			"타코야키—시장 간식",
			"쿠시카츠—신세카이 저녁",
			"키츠네 우동—빠른 한 끼",
		],
		specialties: [
			"551 호라이 돼지고기만두—반입 조건 확인",
			"이와오코시",
			"사카이 칼—위탁·세관 확인",
		],
		prices: {
			transport: "오사카 ¥1,000–1,500",
			meal: "보통 한 끼 ¥900–2,000",
			experience: "전망대·성 ¥600–2,500",
			snack: "간식·커피 ¥400–900",
		},
		spots: [
			"도톤보리—난바",
			"구로몬시장—난바 동쪽",
			"오사카성—JR·지하철",
			"신세카이—텐노지권",
			"교토 기온—하루카+환승",
			"고베 산노미야—공항버스·철도",
		],
	},
	NRT: {
		iata: "NRT",
		city: "도쿄",
		airportName: "나리타국제공항",
		mood: "도쿄까지 거리가 있어 열차의 속도와 버스의 단순함을 비교해야 하는 장거리 관문이에요.",
		fit: "도쿄 장기 · 치바 · 나리타",
		accessSource: "https://www.narita-airport.jp/en/access/train/",
		branches: [
			{
				id: "acc-nrt-01",
				destination: "우에노",
				mode: "게이세이 스카이라이너",
				minutes: "약 41분",
				fare: "공식 시간표에서 확인",
				boarding: "1터미널·2터미널 지하역에서 타고, 3터미널은 역까지 걸어요.",
				fallback: "운휴 시 도쿄역 방면 버스와 JR을 비교해요.",
			},
			{
				id: "acc-nrt-02",
				destination: "도쿄역",
				mode: "나리타 익스프레스·저가버스",
				minutes: "교통편별 확인",
				fare: "버스 ¥1,500 기준",
				boarding: "숙소 위치와 짐에 따라 철도와 버스를 골라요.",
				fallback: "심야에는 공식 막차와 공항 숙박을 함께 확인해요.",
			},
			{
				id: "acc-nrt-03",
				destination: "신주쿠",
				mode: "나리타 익스프레스",
				minutes: "약 80–90분",
				fare: "공식 시간표에서 확인",
				boarding: "신주쿠 직통 편인지 먼저 확인해요.",
				fallback: "직통이 없으면 우에노나 도쿄역을 경유해요.",
			},
		],
		foods: [
			"에도마에 스시—도쿄 동쪽",
			"소바—역 주변 빠른 식사",
			"몬자야키—츠키시마 철판",
			"나리타 장어—첫날·마지막 날",
		],
		specialties: ["피너츠 모나카", "요칸", "나리타산 참배길 센베이"],
		prices: {
			transport: "도쿄 ¥1,500–3,500",
			meal: "보통 한 끼 ¥1,000–2,200",
			experience: "미술관·전망대 ¥1,000–3,500",
			snack: "간식·커피 ¥400–900",
		},
		spots: [
			"나리타산 신쇼지—공항 근교",
			"아사쿠사—게이세이·지하철",
			"우에노공원—스카이라이너 종점권",
			"도쿄역·마루노우치—N'EX·버스",
			"시부야 스크램블—도심 환승",
			"가마쿠라—도쿄권 장거리",
		],
	},
	HND: {
		iata: "HND",
		city: "도쿄",
		airportName: "하네다공항",
		mood: "시나가와·하마마쓰초에 빠르게 붙어 늦은 도착이나 짧은 일정에 강한 도심 관문이에요.",
		fit: "도쿄 짧은 일정 · 야간 도착",
		accessSource: "https://tokyo-haneda.com/en/access/train/index.html",
		branches: [
			{
				id: "acc-hnd-01",
				destination: "시나가와",
				mode: "게이큐",
				minutes: "약 15분",
				fare: "공식 시간표에서 확인",
				boarding: "터미널별 게이큐역에서 타요.",
				fallback: "요코하마 방향 열차를 잘못 타지 않도록 행선지를 확인해요.",
			},
			{
				id: "acc-hnd-02",
				destination: "하마마쓰초",
				mode: "도쿄 모노레일",
				minutes: "약 20분",
				fare: "공식 시간표에서 확인",
				boarding: "3터미널 역에서 타고 JR로 이어가요.",
				fallback: "운휴 시 게이큐로 시나가와에 들어가요.",
			},
			{
				id: "acc-hnd-03",
				destination: "요코하마",
				mode: "게이큐",
				minutes: "약 30분",
				fare: "공식 시간표에서 확인",
				boarding: "직통과 가마타 환승편을 구분해요.",
				fallback: "심야에는 공식 공항버스와 택시 승강장을 확인해요.",
			},
		],
		foods: [
			"에도마에 스시—시장·도심",
			"후카가와메시—동쪽 도쿄",
			"텐동—짧은 점심",
			"아나고—하네다·시나가와권",
		],
		specialties: ["도쿄 바나나 계열 과자", "닌교야키", "에도풍 김·쓰쿠다니"],
		prices: {
			transport: "도심 ¥500–800",
			meal: "보통 한 끼 ¥1,000–2,200",
			experience: "정원·전망대 ¥0–3,500",
			snack: "간식·커피 ¥400–900",
		},
		spots: [
			"시나가와—게이큐 관문",
			"하마리큐—모노레일권",
			"긴자—도심 환승",
			"아사쿠사—게이큐 직통 계통",
			"요코하마 미나토미라이—남쪽 직통",
			"가와사키 다이시—공항 근교",
		],
	},
	CTS: {
		iata: "CTS",
		city: "삿포로",
		airportName: "신치토세공항",
		mood: "삿포로뿐 아니라 온천·도남·도동으로 여행이 갈리는 홋카이도 광역 관문이에요.",
		fit: "설경 · 자연 · 철도 여행",
		accessSource: "https://www.hokkaido-airports.com/en/new-chitose/access/jr/",
		branches: [
			{
				id: "acc-cts-01",
				destination: "삿포로",
				mode: "JR 쾌속",
				minutes: "약 30분",
				fare: "공식 시간표에서 확인",
				boarding: "터미널 지하 1층 JR역에서 무환승으로 가요.",
				fallback: "폭설 운휴 시 공항버스를 확인해요.",
			},
			{
				id: "acc-cts-02",
				destination: "도마코마이",
				mode: "JR 미나미치토세 환승",
				minutes: "약 30분",
				fare: "공식 시간표에서 확인",
				boarding: "미나미치토세에서 방향과 승강장을 확인해요.",
				fallback: "지연이 크면 공항버스를 확인해요.",
			},
			{
				id: "acc-cts-03",
				destination: "오비히로",
				mode: "JR 미나미치토세 환승",
				minutes: "약 2시간 30분",
				fare: "좌석별 확인",
				boarding: "장거리라 지정석과 연결 시간을 확보해요.",
				fallback:
					"폭설 운휴 때는 당일 이동을 강행하지 않고 공항·삿포로 숙박을 봐요.",
			},
		],
		foods: [
			"미소라멘—삿포로 밤",
			"수프카레—채소와 한 그릇",
			"징기스칸—여럿 저녁",
			"해산물덮밥—시장 아침",
		],
		specialties: [
			"시로이 코이비토",
			"로이스 초콜릿",
			"홋카이도 치즈·버터 제품",
		],
		prices: {
			transport: "삿포로 ¥1,000–1,500",
			meal: "보통 한 끼 ¥1,000–2,200",
			experience: "온천·전망대 ¥800–2,500",
			snack: "간식·커피 ¥400–900",
		},
		spots: [
			"삿포로 오도리공원—JR+지하철",
			"니조시장—도심",
			"모이와산—도심 남쪽",
			"오타루 운하—JR 연계",
			"노보리베츠 온천—미나미치토세 방향",
			"비에이·후라노—삿포로·아사히카와 연계",
		],
	},
	OKA: {
		iata: "OKA",
		city: "나하",
		airportName: "나하공항",
		mood: "모노레일 도시 여행과 북부 렌터카 여행의 분기점인 섬 관문이에요.",
		fit: "바다 · 가족 · 드라이브",
		accessSource: "https://www.naha-airport.co.jp/en/access/monorail/",
		branches: [
			{
				id: "acc-oka-01",
				destination: "현청앞·국제거리",
				mode: "유이레일",
				minutes: "약 13분+도보",
				fare: "공식 시간표에서 확인",
				boarding: "2층 연결통로를 따라 모노레일역으로 가요.",
				fallback: "혼잡하거나 짐이 많으면 버스와 택시를 비교해요.",
			},
			{
				id: "acc-oka-02",
				destination: "슈리",
				mode: "유이레일",
				minutes: "약 27분",
				fare: "공식 시간표에서 확인",
				boarding: "슈리 방향 열차를 타요.",
				fallback: "슈리성은 역에서 추가로 걷거나 버스를 타야 해요.",
			},
			{
				id: "acc-oka-03",
				destination: "차탄·온나·나고",
				mode: "공항 리무진·노선버스",
				minutes: "노선별 확인",
				fare: "노선별 확인",
				boarding: "도착장 정류장 번호와 목적지를 함께 확인해요.",
				fallback: "늦은 도착이면 나하 1박이나 렌터카 영업시간을 먼저 봐요.",
			},
		],
		foods: [
			"오키나와 소바—일상 한 끼",
			"고야참푸루—가정식",
			"라후테—돼지고기 조림",
			"사타안다기—시장 간식",
		],
		specialties: ["베니이모 타르트", "친스코", "시콰사 가공품"],
		prices: {
			transport: "나하 ¥300–500",
			meal: "보통 한 끼 ¥800–1,800",
			experience: "성·수족관 ¥400–2,500",
			snack: "간식·커피 ¥300–800",
		},
		spots: [
			"국제거리—유이레일",
			"마키시공설시장—국제거리",
			"슈리성공원—유이레일+도보·버스",
			"세나가지마—공항 근교",
			"아메리칸빌리지—차탄 버스",
			"츄라우미수족관—북부 장거리 버스·렌터카",
		],
	},
	NGO: {
		iata: "NGO",
		city: "나고야",
		airportName: "주부국제공항",
		mood: "나고야를 기준으로 다카야마·이세·기후까지 뻗는 일본 중부 관문이에요.",
		fit: "산업도시 · 알프스 연계",
		accessSource: "https://www.centrair.jp/en/access/train.html",
		branches: [
			{
				id: "acc-ngo-01",
				destination: "나고야",
				mode: "메이테츠 뮤스카이",
				minutes: "최속 28분",
				fare: "좌석별 확인",
				boarding: "Access Plaza 직결역에서 타요.",
				fallback: "뮤티켓이 필요한 열차와 일반 특급을 구분해요.",
			},
			{
				id: "acc-ngo-02",
				destination: "가나야마",
				mode: "메이테츠 직통",
				minutes: "약 25–35분",
				fare: "열차별 확인",
				boarding: "숙소가 남쪽이나 동쪽이면 나고야역보다 편할 수 있어요.",
				fallback: "일반·특급과 정차역을 확인해요.",
			},
			{
				id: "acc-ngo-03",
				destination: "다카야마",
				mode: "메이테츠→나고야→JR·버스",
				minutes: "약 3시간",
				fare: "구간별 확인",
				boarding: "나고야에서 장거리 JR이나 버스로 갈아타요.",
				fallback: "연결 여유가 없으면 나고야에서 1박해요.",
			},
		],
		foods: [
			"히츠마부시—장어를 세 방식으로",
			"미소카츠—진한 된장 소스",
			"키시멘—역 빠른 한 끼",
			"테바사키—저녁 안주",
		],
		specialties: ["우이로", "유카리 새우 센베이", "세토 도자기"],
		prices: {
			transport: "나고야 ¥1,000–1,500",
			meal: "보통 한 끼 ¥900–2,000",
			experience: "성·박물관 ¥500–2,500",
			snack: "간식·커피 ¥400–900",
		},
		spots: [
			"나고야성—도심 지하철",
			"오스상점가—도심",
			"도요타 산업기술기념관—나고야역권",
			"아쓰타신궁—가나야마권",
			"이누야마성—메이테츠",
			"다카야마 산마치—나고야 장거리 연계",
		],
	},
	KMJ: {
		iata: "KMJ",
		city: "구마모토",
		airportName: "구마모토공항",
		mood: "구마모토성 시내와 아소·구로카와 온천 방향이 공항에서 바로 갈리는 지역 관문이에요.",
		fit: "온천 · 화산 · 렌터카",
		accessSource: "https://www.kumamoto-airport.co.jp/en/access/",
		branches: [
			{
				id: "acc-kmj-01",
				destination: "구마모토역",
				mode: "공항 리무진",
				minutes: "약 65분",
				fare: "¥1,200",
				boarding: "4번 승강장에서 타요.",
				fallback: "도로 정체 때는 더 걸릴 수 있어 다음 환승 여유를 둬요.",
			},
			{
				id: "acc-kmj-02",
				destination: "사쿠라마치",
				mode: "특급·일반 버스",
				minutes: "약 45–55분",
				fare: "¥1,200",
				boarding: "특급은 3번, 일반은 4번 승강장을 확인해요.",
				fallback: "좌석과 운행편이 다르니 현장 표지와 공식 시간을 다시 봐요.",
			},
			{
				id: "acc-kmj-03",
				destination: "아소역·구로카와",
				mode: "장거리 버스",
				minutes: "약 60분·120분",
				fare: "¥1,500·¥2,600",
				boarding: "3번 승강장, 예약과 만석 여부를 확인해요.",
				fallback: "직행 실패 시 히고오즈나 구마모토 시내를 경유해요.",
			},
		],
		foods: [
			"구마모토 라멘—마늘향",
			"바사시—허가 업소에서",
			"가라시렌콘—매콤한 반찬",
			"다고지루—아소의 따뜻한 국물",
		],
		specialties: ["진다이코", "이키나리단고", "데코폰·감귤 가공품"],
		prices: {
			transport: "시내 ¥900–1,200",
			meal: "보통 한 끼 ¥800–1,800",
			experience: "성·온천 ¥500–1,500",
			snack: "간식·커피 ¥300–800",
		},
		spots: [
			"구마모토성—사쿠라마치권",
			"스이젠지조주엔—시내 동쪽",
			"가미토리·시모토리—도심 골목",
			"아소산—공항 동쪽 버스·차",
			"구로카와온천—공항 직행 버스",
			"다카치호협곡—예약 버스 장거리",
		],
	},
};

export const editions: Record<AirportCode, Edition> = {
	...coreEditions,
	...additionalEditions,
};

export const photos = photoManifest.assets.map((asset) => ({
	...asset,
	publicPath: `/places/${asset.assetId}.webp`,
}));
export const routes = routeSnapshot.routes;
export const routeSources = routeSnapshot.sources.map((source) => ({
	...source,
	url:
		source.id === "ICN_OFFICIAL_D_PLUS_6"
			? "https://www.airport.kr/ap_ko/869/subview.do"
			: source.url,
}));
export const routeSnapshotMeta = {
	checkedAt: routeSnapshot.checkedAt,
	effectiveDate: routeSnapshot.effectiveDate,
	caveat: routeSnapshot.caveat,
};

export const routesForOrigin = (origin: string) =>
	routes.filter((route) => route.origin === origin);
export {
	currentStaticRoutes,
	isCurrentRoute,
	koreaToday,
} from "./route-freshness";
export const editionPhotos = (iata: string) =>
	photos.filter(
		(photo) => photo.airportIata === iata && photo.status === "active",
	);

export * from "./trip-content";
export * from "./city-guide";
export * from "./gateway-expansion";
export * from "./walks";
export * from "./packing";
